// Registration API endpoint — validated lazily so the app doesn't crash
// when the env var is missing (e.g., local dev without .env)
function getApiEndpoint(): string {
  const endpoint = process.env.REACT_APP_REGISTRATION_API_URL;
  if (!endpoint) {
    throw new Error('REACT_APP_REGISTRATION_API_URL is not configured');
  }
  return endpoint;
}

export interface TokenValidationResponse {
  valid: boolean;
  email?: string;
  company?: string;
  firstName?: string;
  lastName?: string;
  title?: string;
  phone?: string;
  expires?: string;
  error?: string;
}

export interface RegistrationData {
  token: string;
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
  password: string;
}

export interface RegistrationResponse {
  success: boolean;
  userId?: string;
  sessionToken?: string;
  error?: string;
  authResult?: {
    idToken: string;
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
  };
}

export async function validateRegistrationToken(token: string): Promise<TokenValidationResponse> {
  try {
    // NOTE: Backend uses /invitation (singular) for validation
    const response = await fetch(`${getApiEndpoint()}/invitation/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        operation: 'validateInvitation',
        token: token
      })
    });

    const data = await response.json();

    if (response.ok && data.valid) {
      return {
        valid: true,
        email: data.email,
        company: data.company || 'Guest',
        firstName: data.firstName,
        lastName: data.lastName,
        title: data.jobTitle,
        phone: data.phone,
        expires: data.expiresAt
      };
    } else {
      if (response.status === 404) {
        return { valid: false, error: 'Invalid invitation token' };
      } else if (response.status === 410) {
        return { valid: false, error: 'This invitation has expired' };
      } else if (response.status === 409) {
        return { valid: false, error: 'This invitation has already been used' };
      } else {
        return { valid: false, error: data.error || 'Failed to validate invitation' };
      }
    }
  } catch (error) {
    console.error('Error validating token:', error);
    return {
      valid: false,
      error: 'Unable to validate invitation. Please try again later.'
    };
  }
}

export async function registerUser(data: RegistrationData): Promise<RegistrationResponse> {
  try {
    // NOTE: Backend uses /invitations (plural) for completion — inconsistent with
    // /invitation/validate above, but matches the existing working API routes
    const response = await fetch(`${getApiEndpoint()}/invitations/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: data.token,
        firstName: data.firstName,
        lastName: data.lastName,
        title: data.title,
        phone: data.phone,
        password: data.password
      })
    });

    const responseData = await response.json();

    if (response.ok && responseData.success) {
      return {
        success: true,
        userId: responseData.userId,
        sessionToken: responseData.authResult?.idToken,
        authResult: responseData.authResult
      };
    } else {
      if (response.status === 404) {
        return { success: false, error: 'Invalid invitation token' };
      } else if (response.status === 410) {
        return { success: false, error: 'This invitation has expired' };
      } else if (response.status === 409) {
        return { success: false, error: 'This invitation has already been used' };
      } else if (response.status === 400) {
        return { success: false, error: responseData.error || 'Invalid registration data' };
      } else {
        return { success: false, error: responseData.error || 'Registration failed. Please try again.' };
      }
    }
  } catch (error) {
    console.error('Error registering user:', error);
    return {
      success: false,
      error: 'Unable to complete registration. Please try again later.'
    };
  }
}
